import { useEffect } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';

/**
 * Resets the window scroll position on client-side navigation.
 *
 * `BrowserRouter` swaps route content without reloading the document, so the
 * scroll offset of the page you left carries over to the page you land on —
 * which is why changing pages used to drop you into the middle of the new one.
 * This mounts once inside the router and corrects that on every navigation:
 *
 * - **New page (PUSH / REPLACE), no hash** → jump to the top.
 * - **Deep link to a section (`#some-id`)** → scroll that element into view
 *   instead of the top. Lazy route chunks (see `routes.tsx`) may not have
 *   rendered the target by the time the location changes, so we retry across a
 *   few animation frames until it exists (or give up — matching native
 *   "anchor not found" behaviour, which leaves the viewport untouched).
 * - **Back / forward (POP)** → do nothing, so the browser's native scroll
 *   restoration returns the user to where they previously were on that entry.
 *
 * Renders nothing. Lives in the shared `<App />` tree, so the server (SSG) and
 * client trees stay structurally identical (its effect is a no-op during SSR).
 */

/** Animation frames to keep retrying a hash target before giving up. */
const MAX_HASH_RESOLVE_FRAMES = 20;

export function ScrollManager(): null {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // Back/forward: let the browser restore the previous scroll position.
    if (navigationType === NavigationType.Pop) return undefined;

    if (hash) {
      const id = decodeURIComponent(hash.slice(1));
      let frame = 0;
      let rafId = 0;
      let cancelled = false;
      const resolve = (): void => {
        if (cancelled) return;
        const target = id ? document.getElementById(id) : null;
        if (target) {
          target.scrollIntoView();
          return;
        }
        if (frame < MAX_HASH_RESOLVE_FRAMES) {
          frame += 1;
          rafId = requestAnimationFrame(resolve);
        }
      };
      resolve();
      return () => {
        cancelled = true;
        if (rafId) cancelAnimationFrame(rafId);
      };
    }

    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash, navigationType]);

  return null;
}
